import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel, ToggleButton, ToggleButtonGroup, Typography
} from "@mui/material";
import BarChart from "../../../../../../../common/assets/images/Bar-graph.svg";
import LineChart from "../../../../../../../common/assets/images/Line-graph.svg";
import { postJoinedTablesFieldsDataAPI, postJoinedTablesFieldsDataType } from "../../../../../../../redux/saga/partner-details";
import { useAppDispatch, useAppSelector } from "../../../../../../../redux";
import { partnerDetailsSliceAction } from "../../../../../../../redux/slices/partner-details";

type Props = {
  selectedColumns: string[];
  chartSettings: any;
  setChartSettings: (v: any) => void;
  disabled?: boolean;
  readOnly?: boolean;
  columnLabelMap?: Record<string, string>;
  tableColumns?: { value: string; label: string }[];
};

export type ChartSettings = {
  applied: boolean;
  chartType: "line" | "bar";
  title: string;
  xAxis: string;
  yAxis: string;
  field1: string;
  field2: string;
  year: string;
  dateRange: {
    from: string | null;
    to: string | null;
  };
};

const TIME_GRANULARITY = [
  { value: "month", label: "Monthly" },
  { value: "year", label: "Yearly" },
];

export const COUNT_AXIS_OPTION = { value: "count", label: "Count" };

export const AVG_AXIS_OPTION = {
  value: "AVG",
  label: "AVG",
};


const GraphTab: React.FC<Props> = ({
  selectedColumns,
  chartSettings,
  setChartSettings,
  columnLabelMap,
  tableColumns,
  disabled: disabledProp = false,
  readOnly = false,
}) => {
  const [local, setLocal] = useState(chartSettings);
  const { reportFilter }: { reportFilter: postJoinedTablesFieldsDataType } = useAppSelector((state) => state.partner_details);
  const dispatch = useAppDispatch();
  const [errors, setErrors] = useState<{ title?: string }>({});

  const disabled = disabledProp || selectedColumns.length === 0;

  const handleActionGraph = async () => {
    const newErrors: { title?: string } = {};

    if (!local.title.trim()) {
      newErrors.title = "Chart title is required";
    }

    if (!local.chartType) {
      return; // chart type is already visually enforced
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({}); // clear errors

    setChartSettings({
      ...local,
      applied: true,
    });

    const chartPayload = {
      type: local.chartType,
      title: local.title,
      xAxis: local.xAxis,
      yAxis: local.yAxis,
      dateField: local.year,
      dateRange: {
        from: local.dateRange.from,
        to: local.dateRange.to,
      },
       ...(local.yAxis === AVG_AXIS_OPTION.value && {
    aggregation: {
      field1: local.field1,
      field2: local.field2,
    },
  }),
    };


    dispatch(
      partnerDetailsSliceAction.setReportFilterChartAction(chartPayload)
    );

    const normalizedFilters = Array.isArray(reportFilter.filters)
      ? reportFilter.filters
      : reportFilter.filters
      ? [reportFilter.filters]
      : [];

    const payload = {
      report_title: reportFilter.report_title,
      selectColumns: reportFilter.selectColumns,
      filters: normalizedFilters,
      chart: chartPayload,
    };

    const response = await postJoinedTablesFieldsDataAPI(payload);

    if (response?.success && response?.data?.table) {
      dispatch(
        partnerDetailsSliceAction.setShowTableDataAction(response.data.table)
      );
      dispatch(
        partnerDetailsSliceAction.setChartDataAction(response.data.chart)
      );
    }
  };

const handleDateChange = (key: "from" | "to", value: string) => {
  setLocal((prev: ChartSettings) => {
    const updatedSettings = {
      ...prev,
      dateRange: {
        ...prev.dateRange,
        [key]: value,
      },
    };

    setChartSettings(updatedSettings);

    return updatedSettings;
  });
};

  const handleReset = () => {
    setChartSettings({
      applied: false,
      chartType: "",
      title: "",
      xAxis: "",
      yAxis: "",
      field1: "",
      field2: "",
      year: "",
      dateRange: { from: null, to: null },
    });
    setLocal({
      applied: false,
      chartType: "",
      title: "",
      xAxis: "",
      yAxis: "",
      field1: "",
      field2: "",
      year: "",
      dateRange: { from: null, to: null },
    });
  };

  const getColumnLabel = (value: string) => {
    if (value === COUNT_AXIS_OPTION.value) return COUNT_AXIS_OPTION.label;
    if (value === AVG_AXIS_OPTION.value) {
    return AVG_AXIS_OPTION.label;
  }
    return columnLabelMap?.[value] || value;
  };

  const isTimeGranularityDisabled =
  selectedColumns.includes(local.xAxis) && selectedColumns.includes(local.yAxis);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {/* Chart Properties */}
      <div className="rd-filter-section b-bottom" style={{ marginBottom: 15 }}>
        <InputLabel htmlFor="report-title-input" className="label-inline">
          Chart Properties
        </InputLabel>
        <ToggleButtonGroup
          value={local.chartType}
          exclusive
          onChange={(_, value) => {
            if (value) {
              const updatedSettings = {
                ...local,
                chartType: value,
              };

              setLocal(updatedSettings);
              setChartSettings(updatedSettings);
            }
          }}
          size="small"
          sx={{ mt: 1 }}
          disabled={disabled}
        >
          <ToggleButton
            value="line"
            className="graph-toggle-button"
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              border: "none",
            }}
          >
            <img src={LineChart} alt="Line" />
            <Typography variant="caption">Line</Typography>
          </ToggleButton>

          <ToggleButton
            value="bar"
            className="graph-toggle-button"
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              border: "none",
            }}
          >
            <img src={BarChart} alt="Bar" />
            <Typography variant="caption">Bar</Typography>
          </ToggleButton>
        </ToggleButtonGroup>
      </div>

      {/* Chart Attributes */}
      <div className="rd-filter-section" style={{ marginBottom: 15 }}>
        <InputLabel htmlFor="report-title-input" className="label-inline">
          Chart Attributes
        </InputLabel>
        <strong>Chart Title</strong>
        <TextField
          size="small"
          fullWidth
          placeholder="Chart Title"
          value={local.title}
          onChange={e => {
            const updatedSettings = {
              ...local,
              title: e.target.value,
            };

            setLocal(updatedSettings);
            setChartSettings(updatedSettings);

            if (errors.title) {
              setErrors(prev => ({ ...prev, title: undefined }));
            }
          }}
          disabled={disabled}
          error={!!errors.title}
          helperText={errors.title}
        />
      </div>
      <div className="rd-filter-section" style={{ marginBottom: 15 }}>
        <strong>X-Axis</strong>
        <FormControl fullWidth size="small" variant="outlined" disabled={disabled}>
          <Select
            value={local.xAxis}
            label="X-Axis"
            onChange={e => {
              const updatedSettings = {
                ...local,
                xAxis: e.target.value,
              };

              setLocal(updatedSettings);
              setChartSettings(updatedSettings);
            }}
            displayEmpty
            renderValue={(selected) =>
              selected ? getColumnLabel(selected as string) : <span>X-Axis</span>
            }
          >
            <MenuItem value="">
              <span>X-Axis</span>
            </MenuItem>
            {selectedColumns.map(col => (
              <MenuItem key={col} value={col}>
                {getColumnLabel(col)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
      <div className="rd-filter-section b-bottom" style={{ marginBottom: 5, paddingBottom: 15 }}>
        <strong>Y-Axis</strong>
        <FormControl fullWidth size="small" variant="outlined" disabled={disabled}>
          <Select
            value={local.yAxis}
            onChange={(e) => {
              const value = e.target.value;

              const updatedSettings = {
                ...local,
                yAxis: value,
              };

              setLocal(updatedSettings);
              setChartSettings(updatedSettings);
            }}
            displayEmpty
            renderValue={(selected) => {
              if (!selected) return <span>Y-Axis</span>;
              // if (selected === "count") return "Count";
              // if (selected === "sum") return "Sum";
              return getColumnLabel(selected as string);
            }}
          >
            <MenuItem value="">
              <span>Y-Axis</span>
            </MenuItem>

            <MenuItem value={COUNT_AXIS_OPTION.value}>{COUNT_AXIS_OPTION.label}</MenuItem>

            <MenuItem value={AVG_AXIS_OPTION.value}>{AVG_AXIS_OPTION.label}</MenuItem>

            {/* <MenuItem value="sum">Sum</MenuItem> */}

            {selectedColumns.map((col) => (
              <MenuItem key={col} value={col}>
                {getColumnLabel(col)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      {local.yAxis === AVG_AXIS_OPTION.value && (
        <>
        <div className="rd-filter-section" style={{ marginBottom: 15 }}>
          <InputLabel htmlFor="report-title-input" className="label-inline">Average calculation</InputLabel>
          <strong>Calculation field 1</strong>
          
          <FormControl fullWidth size="small" variant="outlined" disabled={disabled}>
            <Select
                value={local.field1 || ""}
                onChange={(e) => {
                const value = e.target.value;

                const updatedSettings = {
                  ...local,
                  field1: value,
                };

                setLocal(updatedSettings);
                setChartSettings(updatedSettings);
              }}
              displayEmpty
              renderValue={(selected) => selected ? getColumnLabel(selected as string) : <span>Select Field 1</span> }
            >
              {selectedColumns.map(col => (
                <MenuItem key={col} value={col}>{getColumnLabel(col)}</MenuItem> ))}
            </Select>
          </FormControl>
        </div>

        <div className="rd-filter-section b-bottom" style={{ marginBottom: 15 }}>
          <strong>Calculation field 2</strong>
          <FormControl fullWidth size="small" variant="outlined" disabled={disabled}>
            <Select
              value={local.field2 || ""}
              onChange={(e) => {
                const value = e.target.value;

                const updatedSettings = {
                  ...local,
                  field2: value,
                };

                setLocal(updatedSettings);
                setChartSettings(updatedSettings);
              }}
              displayEmpty
              renderValue={(selected) => selected ? getColumnLabel(selected as string) : <span>Select Field 2</span>}
            >
              {selectedColumns.map((col) => (
                <MenuItem key={col} value={col}>{getColumnLabel(col)}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
        </>
      )}

      {/* Chart Date */}
      <div className="rd-filter-section" style={{ marginBottom: 5 }}>
        <InputLabel htmlFor="report-title-input" className="label-inline">
          Chart Date
        </InputLabel>
        <strong>Time Granularity</strong>
        <FormControl fullWidth size="small">
          <Select
            value={local.year}
            onChange={e => {
              const updatedSettings = {
                ...local,
                year: e.target.value,
              };

              setLocal(updatedSettings);
              setChartSettings(updatedSettings);
            }}
            displayEmpty
            disabled={disabled || isTimeGranularityDisabled}
            renderValue={(selected) =>
              selected
                ? TIME_GRANULARITY.find(t => t.value === selected)?.label
                : <span>Select granularity</span>
            }
          >
            <MenuItem value="">
              <span>Select granularity</span>
            </MenuItem>
            {TIME_GRANULARITY.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      {/* Date Range */}
      <div className="rd-filter-section" style={{ marginBottom: 15 }}>
        <strong>Date Range</strong>
        <Box sx={{ display: "flex", gap: 3 }}>
          <TextField
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={local.dateRange.from || ""}
            onChange={(e) => handleDateChange("from", e.target.value)}
            // disabled={disabled || !isDateXAxisSelected}
            disabled={disabled}
          />
          <TextField
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={local.dateRange.to || ""}
            onChange={(e) => handleDateChange("to", e.target.value)}
            //disabled={disabled || !isDateXAxisSelected}
            disabled={disabled}
          />
        </Box>
      </div>

      {/* Buttons */}
      {!readOnly && (
        <div className="rd-filter-buttons">
          <Button variant="text" color="primary" className="rd-filter-button-reset" onClick={handleReset} disabled={disabled}>
            Reset
          </Button>
          <Button variant="contained" className="rd-filter-button-apply" onClick={handleActionGraph}>
            Apply
          </Button>
        </div>
      )}
    </Box>
  );
};

export default GraphTab;
